import React, { useState, useEffect, useCallback } from 'react';
import { clinicService } from '../../services/api';

const ClinicSearch = ({ selectedClinicId, onClinicSelect, error, setError }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Search for clinics
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    if (setError) setError('');

    try {
      const response = await clinicService.searchClinicConfigurations({
        name: searchQuery,
        limit: 10
      });

      if (response.success && response.data) {
        setSearchResults(response.data);
      } else {
        if (setError) setError(response.message || 'Failed to search clinics');
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching clinics:', err);
      if (setError) setError(err.response?.data?.detail || err.message || 'An error occurred while searching clinics');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, setError]);

  // Handle clinic selection
  const handleClinicSelect = (clinic) => {
    onClinicSelect(clinic.id);
    setSearchResults([]);
    setSearchQuery('');
  };

  // Handle search input change with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Clinic ID (CLID) *
      </label>

      {/* Search Input */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search by clinic name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchLoading && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={searchLoading || !searchQuery.trim()}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {searchLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching...
            </>
          ) : (
            'Search'
          )}
        </button>
      </div>

      {/* Manual Input */}
      <div className="mb-4">
        <input
          type="text"
          value={selectedClinicId || ''}
          onChange={(e) => onClinicSelect(e.target.value)}
          placeholder="Or enter clinic ID directly (e.g., CLID8855)"
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">You can search for clinics above or enter the clinic ID directly</p>
      </div>

      {/* Search Results */}
      {searchLoading && searchQuery.trim() && (
        <div className="text-center py-4">
          <svg className="animate-spin h-6 w-6 text-blue-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-sm">Searching for clinics...</p>
        </div>
      )}

      {!searchLoading && searchResults.length > 0 && (
        <div className="space-y-2 mb-4">
          <h4 className="font-medium text-gray-700 text-sm">Search Results:</h4>
          {searchResults.map((clinic) => (
            <div
              key={clinic.id}
              className={`p-3 border rounded-md cursor-pointer transition-colors ${
                selectedClinicId === clinic.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => handleClinicSelect(clinic)}
            >
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-gray-900">{clinic.name}</div>
                  <div className="text-sm text-gray-600">ID: {clinic.id}</div>
                  {clinic.region && (
                    <div className="text-sm text-gray-600">Region: {clinic.region}</div>
                  )}
                </div>
                {selectedClinicId === clinic.id && (
                  <div className="text-blue-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
        <div className="text-gray-500 text-center py-2 text-sm">
          No clinics found. Try a different search term or enter the clinic ID manually.
        </div>
      )}

      {/* Selected Clinic Display */}
      {selectedClinicId && !searchQuery && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
          <div className="text-sm text-green-800">
            <span className="font-medium">Selected:</span> {selectedClinicId}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicSearch;

